<script setup>
import { User } from '@pfsc/core/models/user';
import { globalTranslate } from '@pfsc/shared/locale/app.i18n';
import { ref } from 'vue';
import { appValidator } from '@pfsc/core/utils/validator';
import useVuelidate from '@vuelidate/core';
import { rbsService } from '@pfsc/core/services/rbs.service';
import { toggleExecutionState } from '@pfsc/core/utils/toggleExecutionState';
import AppDialog from './app-dialog.vue';
import AppDatePicker from './app-date-picker.vue';

const emits = defineEmits(['on-submitted']);

const dialogRef = ref(null);
const isSubmitting = ref(false);

/** @type {import("vue").Ref<User | null>} */
const selectedUser = ref(null);

const { required } = appValidator;

const rules = ref({
  serverId: { required },
  lastName: { required },
  dob: { required },
});

const initialValue = {
  serverId: '',
  lastName: '',
  dob: null,
  userId: '',
};

const formValue = ref({ ...initialValue });

const v$ = useVuelidate(rules, formValue);

/**
 * Open dialog.
 * @param {User} user User
 */
function openDialog(user) {
  dialogRef.value.openDialog();
  selectedUser.value = user;
  formValue.value = {
    lastName: user.lastName,
    dob: user.dob,
    serverId: user.rbsServerId,
    userId: user.id,
  };
}

/** Close dialog. */
function closeDialog() {
  dialogRef.value.closeDialog();
}

/** Validate RBS server ID. */
async function verifyRbsServerId() {
  const rbsInformation = { serverId: formValue.value.serverId, lastName: formValue.value.lastName, userId: formValue.value.userId };
  await rbsService.verifyRbsInformation(rbsInformation);
}

/** Update rbs information. */
async function updateRbsInformation() {
  const rbsInformation = { serverId: formValue.value.serverId, lastName: formValue.value.lastName, userId: formValue.value.userId };
  await rbsService.updateRbsInformation(rbsInformation, selectedUser.value.id);
}

/** Handle form submit. */
function handleSubmit() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }
  toggleExecutionState(isSubmitting, async () => {
    await verifyRbsServerId();
    await updateRbsInformation();
    emits('on-submitted');
  });
}

defineExpose({ openDialog, closeDialog });
</script>

<template>
  <app-dialog
    ref="dialogRef"
    :has-title-divider="false"
    :content-padding-size="14"
  >
    <v-form :class="$style.section" @submit.prevent="handleSubmit">
      <div>
        <p :class="$style.heading">
          1. {{ globalTranslate('assignment.rbsDialogStep1') }} <a
            :class="$style.rbsLink"
            target="_blank"
            href="https://abcbiz.abc.ca.gov/registration"
          >{{ globalTranslate('assignment.createRbsAccount') }}</a>
        </p>
        <p :class="$style.subHeading">
          {{ globalTranslate('assignment.rbsInstruction3') }}
          <br>
          {{ globalTranslate('assignment.rbsServerIdFee') }}
        </p>
      </div>

      <p :class="$style.heading">
        2. {{ globalTranslate('assignment.enter') }} <span :class="$style.highlight">{{ globalTranslate('assignment.serverId') }}</span>
      </p>

      <div :class="$style.thirdSection">
        <p :class="$style.heading">
          3. {{ globalTranslate('assignment.rbsDialogStep3') }}
        </p>
        <div :class="$style.fieldsWrapper">
          <v-text-field
            v-model="formValue.serverId"
            :error-messages="v$.serverId.$errors.map((e) => e.$message)"
            :label="globalTranslate('assignment.serverId')"
            @blur="v$.serverId.$touch"
          />

          <v-text-field
            v-model="formValue.lastName"
            name="lastName"
            autocomplete="family-name"
            :label="globalTranslate('assignment.lastName')"
            :error-messages="v$.lastName.$errors.map((e) => e.$message)"
            @blur="v$.lastName.$touch"
          />

          <app-date-picker
            v-model="formValue.dob"
            name="dob"
            autocomplete="bday"
            :label="globalTranslate('assignment.dob')"
            density="default"
            :error-messages="v$.dob.$errors.map((e) => e.$message)"
            @blur="v$.dob.$touch"
          />
        </div>
        <button
          v-loading="isSubmitting"
          type="submit"
          :class="['btn btn-form-group btn-orange mx-auto mx-lg-0', $style.submitButton]"
        >
          {{ globalTranslate('button.getStarted') }}
          <v-icon :class="$style.icon" icon="fas fa-caret-right" />
        </button>
      </div>
    </v-form>
  </app-dialog>
</template>

<style module>

.fieldsWrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.submitButton {
  align-self: center;
  width: fit-content;

  /* To overwrite the style btn of bootstrap */
  padding-inline: 40px !important;
}

.icon {
  margin-bottom: 3px;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.heading {
  font-size: var(--font-size-xl);
  font-weight: 700;
  margin-bottom: 0;
}

.subHeading {

  /* Special request from client */
  font-size: 11.5px;
  font-weight: 700;
}

.thirdSection {
  display: flex;
  flex-direction: column;
  gap: 8px
}

.rbsLink {
  color: var(--primary-color);
  text-decoration: underline;
}

.highlight {
  color: var(--primary-color);
}

</style>
